import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Link, Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import html2canvas from 'html2canvas';
import { useAuth } from './AuthContext';
import LoginPage from './LoginPage';
import AdminPage from './AdminPage';
import EmployeeManagementPage from './EmployeeManagementPage';
import EmployeeCalendar from './EmployeeCalendar';
import DailySummary from './DailySummary';
import { DAILY_SUMMARY_ROWS, SUMMARY_COLUMNS, WORK_TYPES } from './workTypes';
import { isTodayInMonth } from './dateUtils';
import './App.css';

const MOBILE_BREAKPOINT = 768;
const OFF_DAY_TYPES = new Set(['holiday', 'saturday', 'sunday']);
const MOBILE_WEEKDAYS = ['일', '월', '화', '수', '목', '금', '토'];

export const processEmployeesForMerging = (employees) => {
    if (!employees || employees.length === 0) return [];

    const processed = employees.map((employee) => ({
        ...employee,
        showDept: false,
        showPart: false,
        showDuty: false
    }));

    for (let i = 0; i < processed.length; i++) {
        if (i === 0 || processed[i].department !== processed[i - 1].department) {
            processed[i].showDept = true;
            let rowSpan = 1;
            for (let j = i + 1; j < processed.length; j++) {
                if (processed[j].department === processed[i].department) rowSpan++;
                else break;
            }
            processed[i].deptRowSpan = rowSpan;
        }

        if (
            i === 0 ||
            processed[i].department !== processed[i - 1].department ||
            processed[i].part !== processed[i - 1].part
        ) {
            processed[i].showPart = true;
            let rowSpan = 1;
            for (let j = i + 1; j < processed.length; j++) {
                if (
                    processed[j].department === processed[i].department &&
                    processed[j].part === processed[i].part
                ) rowSpan++;
                else break;
            }
            processed[i].partRowSpan = rowSpan;
        }

        if (
            i === 0 ||
            processed[i].department !== processed[i - 1].department ||
            processed[i].part !== processed[i - 1].part ||
            processed[i].duty !== processed[i - 1].duty
        ) {
            processed[i].showDuty = true;
            let rowSpan = 1;
            for (let j = i + 1; j < processed.length; j++) {
                if (
                    processed[j].department === processed[i].department &&
                    processed[j].part === processed[i].part &&
                    processed[j].duty === processed[i].duty
                ) rowSpan++;
                else break;
            }
            processed[i].dutyRowSpan = rowSpan;
        }
    }

    return processed;
};

const toDateValue = (year, month, day) => {
    if (!day) return null;
    return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
};

export const isEmployeeInactiveOnDay = (employee, currentDate, day) => {
    const dateValue = toDateValue(currentDate.getFullYear(), currentDate.getMonth() + 1, day);
    if (!dateValue) return true;

    if (employee.hireDate && dateValue < employee.hireDate) {
        return true;
    }

    return Boolean(employee.terminationDate && dateValue > employee.terminationDate);
};

const EditableCell = ({ value, onSave, disabled, workTypeClass }) => {
    const [editing, setEditing] = useState(false);
    const selectRef = useRef(null);

    const handleSave = (newValue) => {
        if (newValue !== value) onSave(newValue);
        setEditing(false);
    };

    useEffect(() => {
        if (editing && selectRef.current) {
            selectRef.current.focus();
        }
    }, [editing]);

    if (disabled) {
        return <div className={`cell-content ${workTypeClass}`}>{value}</div>;
    }

    return (
        <div className="cell-container" onClick={() => !editing && setEditing(true)}>
            {editing ? (
                <select
                    ref={selectRef}
                    value={value || '근무'}
                    onChange={(e) => handleSave(e.target.value)}
                    onBlur={() => setEditing(false)}
                >
                    {WORK_TYPES.map((type) => (
                        <option key={type} value={type}>{type}</option>
                    ))}
                </select>
            ) : (
                <div className={`cell-content ${workTypeClass}`}>{value}</div>
            )}
        </div>
    );
};

const PrivateRoute = ({ children }) => {
    const { user, loading } = useAuth();
    const location = useLocation();

    if (loading) return <div>Loading...</div>;
    return user ? children : <Navigate to="/login" state={{ from: location }} replace />;
};

const MainSchedule = () => {
    const [gridData, setGridData] = useState(null);
    const [currentDate, setCurrentDate] = useState(new Date());
    const [isMobile, setIsMobile] = useState(window.innerWidth <= MOBILE_BREAKPOINT);
    const navigate = useNavigate();
    const printRef = useRef(null);

    const fetchGridData = (year, month) => {
        axios.get('/api/schedules/grid', { params: { year, month } })
            .then((response) => setGridData(response.data))
            .catch((error) => console.error('Error fetching grid data:', error));
    };

    useEffect(() => {
        fetchGridData(currentDate.getFullYear(), currentDate.getMonth() + 1);
    }, [currentDate]);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= MOBILE_BREAKPOINT);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const handleCellSave = (date, employeeName, workType) => {
        axios.put('/api/schedules/cell', { date, employeeName, workType })
            .then(() => fetchGridData(currentDate.getFullYear(), currentDate.getMonth() + 1))
            .catch((error) => {
                alert(`업데이트 실패: ${error.response?.data?.message || '서버 오류'}`);
                fetchGridData(currentDate.getFullYear(), currentDate.getMonth() + 1);
            });
    };

    const changeMonth = (offset) => {
        setCurrentDate((prev) => new Date(prev.getFullYear(), prev.getMonth() + offset, 1));
    };

    const handleDayClick = (day) => {
        if (!day.date) return;
        navigate(`/daily-summary/${currentDate.getFullYear()}/${currentDate.getMonth() + 1}/${day.date}`);
    };

    const handleDownload = async () => {
        const element = printRef.current;
        if (!element) return;

        const canvas = await html2canvas(element, {
            useCORS: true,
            backgroundColor: '#ffffff',
            scale: 2
        });

        const data = canvas.toDataURL('image/png');
        const link = document.createElement('a');
        const year = currentDate.getFullYear();
        const month = String(currentDate.getMonth() + 1).padStart(2, '0');

        link.href = data;
        link.download = `${year}-${month} 메인스케줄.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const renderMobileCalendar = () => {
        const year = currentDate.getFullYear();
        const month = currentDate.getMonth();
        const firstDay = new Date(year, month, 1).getDay();
        const daysInMonth = new Date(year, month + 1, 0).getDate();
        const rows = [];
        let cells = [];

        for (let i = 0; i < firstDay; i++) {
            cells.push(<div key={`empty-${i}`} className="mobile-calendar-cell empty"></div>);
        }

        for (let day = 1; day <= daysInMonth; day++) {
            const dayInfo = gridData?.headerDays?.find((headerDay) => Number(headerDay.date) === day);
            const dayType = dayInfo?.dayType || 'weekday';
            const todayClass = isTodayInMonth(currentDate, day) ? 'today' : '';

            cells.push(
                <button
                    key={day}
                    type="button"
                    className={`mobile-calendar-cell ${dayType} ${todayClass}`}
                    onClick={() => handleDayClick({ date: day })}
                >
                    <span className="mobile-calendar-date">{day}</span>
                </button>
            );

            if (cells.length === 7) {
                rows.push(
                    <div key={`week-${day}`} className="mobile-calendar-row">
                        {cells}
                    </div>
                );
                cells = [];
            }
        }

        if (cells.length > 0) {
            while (cells.length < 7) {
                cells.push(<div key={`empty-end-${cells.length}`} className="mobile-calendar-cell empty"></div>);
            }
            rows.push(
                <div key="week-last" className="mobile-calendar-row">
                    {cells}
                </div>
            );
        }

        return rows;
    };

    const isEditable = gridData?.isEditable || false;
    const processedEmployees = gridData ? processEmployeesForMerging(gridData.employees) : [];
    const monthlyOffDays = (gridData?.headerDays || []).reduce((count, day) => (
        OFF_DAY_TYPES.has(day.dayType) ? count + 1 : count
    ), 0);
    const monthlyHolidayCount = 0;
    const monthlySaturdayCount = 0;
    const monthlySundayCount = 0;
    const monthLabel = `${currentDate.getFullYear()}년 ${currentDate.getMonth() + 1}월`;

    return (
        <>
            <header className="App-header">
                <h1>아트몰링 스케줄</h1>
                <div className="month-navigator">
                    <button onClick={() => changeMonth(-1)}>&lt;</button>
                    <span>{monthLabel}</span>
                    <button onClick={() => changeMonth(1)}>&gt;</button>
                </div>
                <div className="monthly-off-summary">
                    {`휴무 ${monthlyOffDays}일 (공휴일 ${monthlyHolidayCount} / 토 ${monthlySaturdayCount} / 일 ${monthlySundayCount})`}
                </div>
                <div className="monthly-off-summary-simple">
                    {`휴무 ${monthlyOffDays}일`}
                </div>
                {!isMobile && (
                    <button onClick={handleDownload} className="download-button">다운로드</button>
                )}
            </header>

            <main className="grid-container">
                {gridData ? (
                    isMobile ? (
                        <div className="mobile-calendar">
                            <div className="mobile-calendar-weekdays">
                                {MOBILE_WEEKDAYS.map((label) => (
                                    <div key={label} className="mobile-calendar-weekday">{label}</div>
                                ))}
                            </div>
                            <div className="mobile-calendar-grid">
                                {renderMobileCalendar()}
                            </div>
                        </div>
                    ) : (
                        <table className="schedule-grid" ref={printRef}>
                            <thead>
                                <tr>
                                    <th rowSpan="2" className="info-col">부서</th>
                                    <th rowSpan="2" className="info-col">파트</th>
                                    <th rowSpan="2" className="info-col">직무</th>
                                    <th rowSpan="2" className="info-col">직급</th>
                                    <th rowSpan="2" className="info-col">성명</th>
                                    {gridData.headerDays.map((day, index) => (
                                        <th
                                            key={`date-${index}`}
                                            className={`day-type-${day.dayType} day-col ${isTodayInMonth(currentDate, day.date) ? 'today-col' : ''}`}
                                            onClick={() => handleDayClick(day)}
                                        >
                                            <div className="date-header">{day.date}</div>
                                        </th>
                                    ))}
                                    <th colSpan={SUMMARY_COLUMNS.length} className="summary-title">총계</th>
                                </tr>
                                <tr>
                                    {gridData.headerDays.map((day, index) => (
                                        <th
                                            key={`weekday-${index}`}
                                            className={`day-type-${day.dayType} day-col ${isTodayInMonth(currentDate, day.date) ? 'today-col' : ''}`}
                                            onClick={() => handleDayClick(day)}
                                        >
                                            <div className="weekday-header">{day.dayOfWeek}</div>
                                        </th>
                                    ))}
                                    {SUMMARY_COLUMNS.map((column) => (
                                        <th key={column} className="summary-head-col">{column}</th>
                                    ))}
                                </tr>
                            </thead>

                            <tbody>
                                {processedEmployees.map((employee) => (
                                    <tr key={employee.id}>
                                        {employee.showDept && <td rowSpan={employee.deptRowSpan} className="info-col">{employee.department}</td>}
                                        {employee.showPart && <td rowSpan={employee.partRowSpan} className="info-col">{employee.part}</td>}
                                        {employee.showDuty && <td rowSpan={employee.dutyRowSpan} className="info-col">{employee.duty}</td>}
                                        <td className="info-col">{employee.position}</td>
                                        <td className="info-col">
                                            <Link to={`/employee/${employee.name}`}>{employee.name}</Link>
                                        </td>

                                        {gridData.headerDays.map(({ date, dayType }, index) => {
                                            const employmentInactive = isEmployeeInactiveOnDay(employee, currentDate, date);
                                            const workType = employmentInactive ? '' : gridData.data[employee.name]?.[date] || '';
                                            const fullDate = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(date).padStart(2, '0')}`;

                                            return (
                                                <td key={`${date}-${index}`} className={`day-type-${dayType} day-col ${employmentInactive ? 'employment-inactive' : ''} ${isTodayInMonth(currentDate, date) ? 'today-col' : ''}`}>
                                                    <EditableCell
                                                        value={workType}
                                                        onSave={(newWorkType) => handleCellSave(fullDate, employee.name, newWorkType)}
                                                        disabled={dayType === 'empty' || employmentInactive || !isEditable}
                                                        workTypeClass={workType ? `work-type-cell-${workType}` : ''}
                                                    />
                                                </td>
                                            );
                                        })}

                                        {SUMMARY_COLUMNS.map((column) => (
                                            <td key={`${employee.id}-${column}`} className="summary-cell summary-body-col">
                                                {gridData.employeeSummaries[employee.name]?.[column] ?? 0}
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>

                            <tfoot>
                                {DAILY_SUMMARY_ROWS.map((key) => (
                                    <tr key={key} className="summary-footer-row">
                                        <td colSpan="5" className="footer-label">{key}</td>
                                        {gridData.headerDays.map((day, index) => (
                                            <td key={`footer-${day.date}-${index}`} className={`day-type-${day.dayType} day-col ${isTodayInMonth(currentDate, day.date) ? 'today-col' : ''}`}>
                                                {day.dayType !== 'empty' ? gridData.dailySummaries[day.date]?.[key] : ''}
                                            </td>
                                        ))}
                                        <td colSpan={SUMMARY_COLUMNS.length} className="summary-blank"></td>
                                    </tr>
                                ))}
                            </tfoot>
                        </table>
                    )
                ) : (
                    <p>스케줄 데이터가 없습니다.</p>
                )}
            </main>
        </>
    );
};

function App() {
    return (
        <div className="App">
            <Routes>
                <Route path="/" element={<MainSchedule />} />
                <Route path="/login" element={<LoginPage />} />
                <Route path="/admin" element={<PrivateRoute><AdminPage /></PrivateRoute>} />
                <Route path="/admin/employees" element={<PrivateRoute><EmployeeManagementPage /></PrivateRoute>} />
                <Route path="/employee/:employeeName" element={<EmployeeCalendar />} />
                <Route path="/daily-summary/:year/:month/:day" element={<DailySummary />} />
            </Routes>
        </div>
    );
}

export default App;
